import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import { FetchHoldingCompanyResponse, FetchHoldingCompaniesResponse } from '../../../types/holdingCompany';
import {
  fetchHoldingCompany,
  fetchHoldingCompanies,
  resetEditHoldingCompany,
  cleanHoldingCompanies,
  updateHoldingCompany,
  createHoldingCompany,
  deleteHoldingCompany,
} from './holdingCompany.actions';
import { HoldingCompanyState } from './holdingCompany.types';

const initialState: HoldingCompanyState = {
  holdingCompanies: [],
  totalHoldingCompanies: 0,
  editHoldingCompany: null,
  fetchStatus: fetchStatuses.idle,
  deleteStatus: fetchStatuses.idle,
  updateStatus: fetchStatuses.idle,
  createStatus: fetchStatuses.idle,
  error: null,
};

const holdingCompaniesSlice = createSlice({
  name: 'holdingCompanies',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchHoldingCompanies.pending, (state) => {
      state.fetchStatus = fetchStatuses.pending;
      state.deleteStatus = fetchStatuses.idle;
      state.updateStatus = fetchStatuses.idle;
      state.createStatus = fetchStatuses.idle;
    });
    builder.addCase(fetchHoldingCompanies.fulfilled, (state, action: PayloadAction<FetchHoldingCompaniesResponse>) => {
      state.holdingCompanies = action.payload.data?.items;
      state.totalHoldingCompanies = action.payload.data?.totalItems;
      state.fetchStatus = fetchStatuses.success;
    });
    builder.addCase(fetchHoldingCompanies.rejected, (state, action) => {
      state.fetchStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    builder.addCase(fetchHoldingCompany.pending, (state) => {
      state.fetchStatus = fetchStatuses.pending;
      state.deleteStatus = fetchStatuses.idle;
      state.updateStatus = fetchStatuses.idle;
      state.createStatus = fetchStatuses.idle;
    });
    builder.addCase(fetchHoldingCompany.fulfilled, (state, action: PayloadAction<FetchHoldingCompanyResponse>) => {
      state.editHoldingCompany = action.payload.data;
      state.fetchStatus = fetchStatuses.success;
    });
    builder.addCase(fetchHoldingCompany.rejected, (state, action) => {
      state.fetchStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    builder.addCase(updateHoldingCompany.pending, (state) => {
      state.error = null;
      state.updateStatus = fetchStatuses.pending;
      state.fetchStatus = fetchStatuses.idle;
      state.deleteStatus = fetchStatuses.idle;
      state.createStatus = fetchStatuses.idle;
    });
    builder.addCase(updateHoldingCompany.fulfilled, (state) => {
      state.updateStatus = fetchStatuses.success;
    });
    builder.addCase(updateHoldingCompany.rejected, (state, action) => {
      state.updateStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    builder.addCase(createHoldingCompany.pending, (state) => {
      state.error = null;
      state.createStatus = fetchStatuses.pending;
      state.fetchStatus = fetchStatuses.idle;
      state.deleteStatus = fetchStatuses.idle;
      state.updateStatus = fetchStatuses.idle;
    });
    builder.addCase(createHoldingCompany.fulfilled, (state, action: PayloadAction<FetchHoldingCompanyResponse>) => {
      state.editHoldingCompany = action.payload.data;
      state.createStatus = fetchStatuses.success;
    });
    builder.addCase(createHoldingCompany.rejected, (state, action) => {
      state.createStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    builder.addCase(deleteHoldingCompany.pending, (state) => {
      state.error = null;
      state.deleteStatus = fetchStatuses.pending;
      state.fetchStatus = fetchStatuses.idle;
      state.updateStatus = fetchStatuses.idle;
      state.createStatus = fetchStatuses.idle;
    });
    builder.addCase(deleteHoldingCompany.fulfilled, (state) => {
      state.editHoldingCompany = null;
      state.deleteStatus = fetchStatuses.success;
    });
    builder.addCase(deleteHoldingCompany.rejected, (state, action) => {
      state.deleteStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    builder.addCase(resetEditHoldingCompany.type, (state) => {
      state.editHoldingCompany = null;
      state.fetchStatus = fetchStatuses.idle;
      state.deleteStatus = fetchStatuses.idle;
      state.updateStatus = fetchStatuses.idle;
      state.createStatus = fetchStatuses.idle;
      state.error = null;
    });

    builder.addCase(cleanHoldingCompanies.type, (state) => {
      Object.assign(state, initialState);
    });
  },
});

export const holdingCompanyReducer = holdingCompaniesSlice.reducer;
